// HUDScene: interfaz superpuesta en espacio de PANTALLA (responsive) que también
// aloja los controles táctiles. En horizontal replica el diseño actual; en vertical
// coloca la pregunta y stats en la banda superior y los controles en la inferior.

import Phaser from 'phaser';
import { HEX, CSS, FONTS, LEVEL, getGameplayUiTheme } from '../config';
import { getBands } from '../util/responsive';
import { ProgressData as PlayerData } from '../systems/ProgressData';
import { formatScore } from '../util/numberFormat';
import { PauseMenu } from '../ui/PauseMenu';

export class HUDScene extends Phaser.Scene {
  constructor() {
    super('HUDScene');
  }

  create() {
    this.gs = this.registry.get('gameState');
    this.audio = this.registry.get('audio');
    this.ls = this.scene.get('LevelScene');
    this.worldTheme = getGameplayUiTheme(this.ls?.planetId || this.registry.get('currentPlanet') || 'suma');
    this.isTouch =
      (this.sys.game.device.input && this.sys.game.device.input.touch) ||
      (typeof window !== 'undefined' &&
        (window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0));

    this.ui = this.add.container(0, 0).setDepth(100);
    this.controlResets = [];
    this.pauseMenu = null;
    this.tutorialCard = null;
    this.tutorialPointer = null;
    this.tutorialDisplayStep = null;
    this.tutorialFirstZoneShown = false;
    this.answerGuideVisible = false;
    this.onTutorialStep = ({ step }) => this.showTutorialStep(step);
    this.onTutorialComplete = () => {
      this.tutorialDisplayStep = null;
      this.destroyTutorialCard();
      this.destroyTutorialPointer();
      this.clearAnswerGuide();
    };
    this.onTutorialFirstZone = () => this.showFirstAnswerHint();
    this.ls.events.on('tutorial-step', this.onTutorialStep);
    this.ls.events.on('tutorial-complete', this.onTutorialComplete);
    this.ls.events.on('tutorial-first-zone', this.onTutorialFirstZone);
    this.releaseControls = () => {
      this.controlResets.forEach(reset => reset());
      ['left', 'right', 'jump'].forEach(key => this.ls.setControl(key, false));
      if (this.ls.touch) this.ls.touch.jumpPressed = false;
    };
    window.addEventListener('blur', this.releaseControls);
    window.addEventListener('touchcancel', this.releaseControls);
    const onVisibility = () => { if (document.hidden) this.releaseControls(); };
    document.addEventListener('visibilitychange', onVisibility);
    this.layout();
    this.scale.on('resize', this.layout, this);
    this.events.once('shutdown', () => {
      this.scale.off('resize', this.layout, this);
      this.ls.events.off('tutorial-step', this.onTutorialStep);
      this.ls.events.off('tutorial-complete', this.onTutorialComplete);
      this.ls.events.off('tutorial-first-zone', this.onTutorialFirstZone);
      this.destroyTutorialCard();
      this.destroyTutorialPointer();
      this.pauseMenu?.destroy();
      this.pauseMenu = null;
      window.removeEventListener('blur', this.releaseControls);
      window.removeEventListener('touchcancel', this.releaseControls);
      document.removeEventListener('visibilitychange', onVisibility);
      this.releaseControls();
    });
  }

  panel(x, y, w, h, color, alpha, radius) {
    const g = this.add.graphics();
    g.fillStyle(0x150e38, alpha);
    g.fillRoundedRect(x, y, w, h, radius);
    g.lineStyle(3, color, 0.9);
    g.strokeRoundedRect(x, y, w, h, radius);
    this.ui.add(g);
    return g;
  }

  // Cápsulas de contraste para que los datos importantes siempre se lean,
  // incluso sobre planetas luminosos como Resta y División.
  hudCapsule(x, y, w, h, accent, radius = 16) {
    const g = this.add.graphics();
    g.fillStyle(0x080622, 0.82);
    g.fillRoundedRect(x, y, w, h, radius);
    g.lineStyle(2, accent, 0.72);
    g.strokeRoundedRect(x, y, w, h, radius);
    this.ui.add(g);
    return g;
  }

  text(x, y, str, size, color, origin = 0.5, font = FONTS.display, style = '900') {
    const t = this.add
      .text(x, y, str, { fontFamily: font, fontSize: `${Math.round(size)}px`, fontStyle: style, color })
      .setOrigin(origin === 0 ? 0 : origin, 0.5);
    this.ui.add(t);
    return t;
  }

  // Guía de entrada: en táctil señala físicamente cada botón y valida el
  // movimiento real. No usa mensajes genéricos ni avanza por tiempo.
  destroyTutorialCard() {
    if (!this.tutorialCard) return;
    this.tutorialCard.forEach(item => item?.destroy?.());
    this.tutorialCard = null;
  }

  destroyTutorialPointer() {
    if (!this.tutorialPointer) return;
    this.tutorialPointer.forEach(item => item?.destroy?.());
    this.tutorialPointer = null;
  }

  showTutorialCard(title, message, duration = 0, keySymbol = null) {
    this.destroyTutorialCard();
    const W = this.scale.width;
    const H = this.scale.height;
    const bands = getBands(W, H);
    const portrait = bands.portrait;
    const cardW = Math.min(W - 28, portrait ? 390 : 510);
    const cardH = keySymbol ? (portrait ? 126 : 132) : (portrait ? 88 : 94);
    const cx = W / 2;
    const cy = portrait
      ? Math.max(cardH / 2 + 8, bands.playY - cardH / 2 - 14)
      : Math.max(164, H * 0.24);
    const left = cx - cardW / 2;
    const top = cy - cardH / 2;
    const panel = this.add.graphics().setDepth(190);
    panel.fillStyle(0x061329, 0.94);
    panel.fillRoundedRect(left, top, cardW, cardH, 18);
    panel.lineStyle(3, HEX.cyan, 0.95);
    panel.strokeRoundedRect(left, top, cardW, cardH, 18);

    const titleText = this.add.text(cx, top + 20, title, {
      fontFamily: FONTS.display,
      fontSize: portrait ? '16px' : '19px',
      fontStyle: '900',
      color: CSS.gold,
    }).setOrigin(0.5).setDepth(191);
    const messageText = this.add.text(cx, top + 57, message, {
      fontFamily: FONTS.friendly,
      fontSize: portrait ? '14px' : '17px',
      fontStyle: '800',
      color: CSS.white,
      align: 'center',
      wordWrap: { width: cardW - 28, useAdvancedWrap: true },
    }).setOrigin(0.5).setDepth(191);

    const card = [panel, titleText, messageText];
    // En computadora la tecla se ve como una tecla real, no como texto suelto.
    if (keySymbol) {
      const keyW = portrait ? 46 : 54;
      const keyH = portrait ? 36 : 42;
      const keyY = top + cardH - keyH / 2 - 10;
      const keyCap = this.add.graphics().setDepth(191);
      keyCap.fillStyle(0x0b2450, 1);
      keyCap.fillRoundedRect(cx - keyW / 2, keyY - keyH / 2, keyW, keyH, 8);
      keyCap.lineStyle(2, HEX.cyan, 0.95);
      keyCap.strokeRoundedRect(cx - keyW / 2, keyY - keyH / 2, keyW, keyH, 8);
      const keyText = this.add.text(cx, keyY, keySymbol, {
        fontFamily: FONTS.display,
        fontSize: portrait ? '24px' : '29px',
        fontStyle: '900',
        color: CSS.white,
      }).setOrigin(0.5).setDepth(192);
      card.push(keyCap, keyText);
    }
    card.forEach(item => item.setAlpha(0));
    this.tutorialCard = card;
    this.tweens.add({ targets: card, alpha: 1, duration: 160, ease: 'Sine.out' });
    if (duration > 0) {
      this.time.delayedCall(duration, () => {
        if (this.tutorialCard !== card) return;
        this.destroyTutorialCard();
      });
    }
  }

  showTutorialPointer(key) {
    this.destroyTutorialPointer();
    if (!this.isTouch || !this.controlButtons?.[key]) return;
    const { x, y, r } = this.controlButtons[key];
    const arrow = this.add.text(x, y - r - 30, '↓', {
      fontFamily: FONTS.display,
      fontSize: '42px',
      fontStyle: '900',
      color: CSS.gold,
    }).setOrigin(0.5).setDepth(220).setStroke('#061329', 5);
    const controlTint = this.worldTheme?.tint || HEX.gold;
    const ring = this.add.circle(x, y, r + 11, controlTint, 0)
      .setStrokeStyle(3, controlTint, 0.95)
      .setDepth(219);
    this.tutorialPointer = [arrow, ring];
    this.tweens.add({
      targets: [arrow, ring],
      y: '-=10',
      duration: 420,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  // La indicación final reutiliza la cápsula de misión ya existente.
  // Así nunca se superpone a las respuestas ni a las plataformas en celular.
  showAnswerGuide() {
    this.destroyTutorialCard();
    this.answerGuideVisible = true;
    if (!this.missionText) return;
    this.missionText.setText('BRINCA A LA RESPUESTA CORRECTA').setColor(CSS.gold);
    this.missionText.setScale(Math.min(1, this.missionMaxW / Math.max(1, this.missionText.width)));
  }

  clearAnswerGuide() {
    if (!this.answerGuideVisible) return;
    this.answerGuideVisible = false;
    if (!this.missionText) return;
    this.missionText.setText(this.getMissionLabel()).setColor(CSS.white);
    this.missionText.setScale(Math.min(1, this.missionMaxW / Math.max(1, this.missionText.width)));
  }

  showTutorialStep(step) {
    this.tutorialDisplayStep = step;
    this.destroyTutorialPointer();
    if (step === 'answer') {
      this.showAnswerGuide();
      return;
    }
    const messages = {
      right: ['VAMOS', 'Dale a la derecha.'],
      left: ['MUY BIEN', 'Ahora dale a la izquierda.'],
      jump: ['EXCELENTE', 'Ahora brinca.'],
      success: ['✓ ¡BIEN!', ''],
    };
    const [title, message] = messages[step] || messages.right;
    const keyboardKeys = { right: '→', left: '←', jump: '↑' };
    const keySymbol = this.isTouch ? null : keyboardKeys[step];
    this.showTutorialCard(title, message, step === 'success' ? 260 : 0, keySymbol);
    if (step === 'right' || step === 'left' || step === 'jump') this.showTutorialPointer(step);
  }

  showFirstAnswerHint() {
    // Conservado para partidas futuras sin guía; durante el primer recorrido
    // guiado, la instrucción "answer" ya permanece visible hasta acertar.
    if (this.tutorialFirstZoneShown) return;
    this.tutorialFirstZoneShown = true;
    this.tutorialDisplayStep = null;
    this.showTutorialCard('PRIMER RETO', 'Brinca a la respuesta correcta.', 3400);
  }

  // El círculo visible es fino; el área táctil es deliberadamente más grande.
  // Así se ve limpio sin pedir precisión excesiva a niños.
  ctrlButton(x, y, r, glyph, color, key) {
    const c = this.add.circle(x, y, r, color, 0.18).setStrokeStyle(2.5, color, 0.82);
    const icon = this.add.graphics();
    const s = r * 0.46;
    icon.fillStyle(0xffffff, 0.98);
    if (glyph === '◀') icon.fillTriangle(x - s, y, x + s * 0.72, y - s * 0.82, x + s * 0.72, y + s * 0.82);
    else if (glyph === '▶') icon.fillTriangle(x + s, y, x - s * 0.72, y - s * 0.82, x - s * 0.72, y + s * 0.82);
    else icon.fillTriangle(x, y - s, x - s * 0.82, y + s * 0.72, x + s * 0.82, y + s * 0.72);
    this.ui.add([c, icon]);
    this.controlButtons[key] = { x, y, r };

    // El área real supera el mínimo recomendado para toque, aunque el botón
    // visible no tape el escenario.
    const hitRadius = Math.max(48, r + 11);
    // El área interactiva usa coordenadas locales del círculo, no las de la pantalla.
    c.setInteractive(new Phaser.Geom.Circle(r, r, hitRadius), Phaser.Geom.Circle.Contains);
    const pointers = new Set();
    this.controlResets.push(() => {
      pointers.clear();
      if (c.active) c.setFillStyle(color, 0.18);
      if (icon.active) icon.setAlpha(0.98);
    });
    c.on('pointerdown', pointer => {
      pointers.add(pointer.id);
      this.ls.setControl(key, true);
      c.setFillStyle(color, 0.42);
      icon.setAlpha(1);
    });
    const rel = pointer => {
      pointers.delete(pointer.id);
      if (!pointers.size) {
        this.ls.setControl(key, false);
        c.setFillStyle(color, 0.18);
        icon.setAlpha(0.98);
      }
    };
    c.on('pointerup', rel);
    c.on('pointerout', rel);
    this.input.on('pointerup', rel);
    this.input.on('pointerupoutside', rel);
    c.once('destroy', () => {
      this.input.off('pointerup', rel);
      this.input.off('pointerupoutside', rel);
    });
    return c;
  }

  getMissionLabel() {
    const mission = this.registry.get('currentMissionData');
    if (!mission) return '';
    return `${mission.name} · ${mission.skill}`;
  }

  layout() {
    this.destroyTutorialCard();
    this.destroyTutorialPointer();
    this.releaseControls();
    this.controlResets = [];
    this.controlButtons = {};
    this.ui.removeAll(true);
    this.isTouch = this.sys.game.device.input.touch ||
      window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
    const W = this.scale.width;
    const H = this.scale.height;
    const b = getBands(W, H);
    this.portrait = b.portrait;
    // Only cover the unused bands, never the playable camera viewport.
    if (b.portrait) {
      // Tile two cropped images into the bands; the middle stays transparent.
      const topBg = this.add.image(W / 2, b.top / 2, 'bg_deepspace').setDisplaySize(W, b.top).setAlpha(0.45);
      const bottomBg = this.add.image(W / 2, b.playY + b.playH + b.bottom / 2, 'bg_deepspace').setDisplaySize(W, b.bottom).setAlpha(0.45);
      this.ui.add([topBg, bottomBg]);
    }

    if (b.portrait) this.layoutPortrait(W, H, b);
    else this.layoutLandscape(W, H);
    // Al girar la pantalla, vuelve a mostrar el paso activo sin reiniciar la guía.
    if (this.tutorialDisplayStep) this.time.delayedCall(0, () => this.showTutorialStep(this.tutorialDisplayStep));
    else if (this.answerGuideVisible) this.clearAnswerGuide();
    this.pauseMenu?.relayout();
  }

  layoutLandscape(W, H) {
    const s = H / 720;
    const worldTint = this.worldTheme?.tint || HEX.cyan;
    // Panel de operación (centro superior).
    const panelW = Math.min(W * 0.52, Math.max(280, 440 * s));
    const panelH = Math.max(44, 78 * s);
    this.opMaxW = panelW - 24;
    this.panel(W / 2 - panelW / 2, 16 * s, panelW, panelH, worldTint, 0.85, 20 * s);
    this.opText = this.text(W / 2, 16 * s + panelH / 2, '', Math.max(24, 38 * s), CSS.white);

    // Etiqueta de destreza: siempre muestra qué está practicando el estudiante.
    const missionY = 16 * s + panelH + 10 * s;
    this.missionMaxW = panelW - 20;
    this.hudCapsule(W / 2 - panelW / 2, missionY, panelW, Math.max(24, 28 * s), worldTint, 13 * s);
    this.missionText = this.text(W / 2, missionY + Math.max(12, 14 * s), this.getMissionLabel(), Math.max(12, 16 * s), CSS.white, 0.5, FONTS.friendly, '800');
    this.missionText.setScale(Math.min(1, this.missionMaxW / Math.max(1, this.missionText.width)));

    // Datos en las mismas esquinas, ahora protegidos por cápsulas oscuras.
    const statsW = Math.max(126, 178 * s);
    const statsH = Math.max(68, 94 * s);
    const statsX = 12 * s;
    const statsY = 12 * s;
    this.hudCapsule(statsX, statsY, statsW, statsH, worldTint, 18 * s);
    this.scoreText = this.text(statsX + 14 * s, statsY + 26 * s, '', Math.max(18, 30 * s), CSS.gold, 0);
    this.streakText = this.text(statsX + 14 * s, statsY + 66 * s, '', Math.max(16, 22 * s), CSS.magenta, 0, FONTS.friendly, '800');

    const vitalW = Math.max(142, 194 * s);
    const vitalH = Math.max(94, 112 * s);
    const vitalX = W - vitalW - 12 * s;
    const vitalY = 12 * s;
    this.hudCapsule(vitalX, vitalY, vitalW, vitalH, worldTint, 18 * s);
    this.timeText = this.text(vitalX + vitalW - 14 * s, vitalY + 26 * s, '', Math.max(18, 30 * s), CSS.cyan, 1);

    this.lifeOrbs = [];
    for (let i = 0; i < LEVEL.MAX_LIVES; i++) {
      const orb = this.add.image(vitalX + vitalW - 28 * s - i * 42 * s, vitalY + 72 * s, 'crystal').setScale(0.7 * s).setTint(HEX.red);
      this.ui.add(orb);
      this.lifeOrbs.push(orb);
    }

    // Progreso: una cápsula unificada para que "Retos 0/10" y la barra
    // sigan siendo legibles sobre cualquiera de los paisajes.
    const barW = 520 * s;
    const progressH = this.isTouch ? 42 : Math.max(52, 62 * s);
    // En móvil se eleva un poco para dejar una franja limpia a los pulgares.
    const progressY = this.isTouch ? H - progressH - 16 : H - progressH - 22 * s;
    this.hudCapsule(W / 2 - barW / 2 - 16 * s, progressY, barW + 32 * s, progressH, worldTint, 16 * s);
    this.bar = {
      x: W / 2 - barW / 2,
      y: progressY + (this.isTouch ? 28 : 37 * s),
      w: barW,
      h: this.isTouch ? 6 : 12 * s,
    };
    this.progFill = this.add.graphics();
    this.ui.add(this.progFill);
    this.progLabel = this.text(
      W / 2,
      progressY + (this.isTouch ? 14 : 18 * s),
      '',
      Math.max(16, 19 * s),
      CSS.white,
      0.5,
      FONTS.friendly,
      '800',
    );

    this.buildMute(W - 32, 120 * s + 20, 20, 0.5);
    // Pausa: siempre debajo y centrada con el panel superior izquierdo.
    const pauseRadius = this.isTouch ? Phaser.Math.Clamp(25 * s, 24, 27) : 22;
    const pauseGap = Phaser.Math.Clamp(12 * s, 8, 16);
    this.buildPauseButton(
      statsX + statsW / 2,
      statsY + statsH + pauseGap + pauseRadius,
      pauseRadius,
    );

    if (this.isTouch) {
      // Botones más discretos en pantalla, con una zona táctil amplia definida arriba.
      const rr = Phaser.Math.Clamp(Math.min(W, H) * 0.095, 42, 58);
      const by = H - rr - 8;
      this.ctrlButton(rr + 18, by, rr, '◀', worldTint, 'left');
      this.ctrlButton(rr + 18 + rr * 2.15, by, rr, '▶', worldTint, 'right');
      this.ctrlButton(W - rr - 18, by, rr, '▲', worldTint, 'jump');
    }
  }

  layoutPortrait(W, H, b) {
    const top = b.top;
    const worldTint = this.worldTheme?.tint || HEX.cyan;
    // Banda superior: pregunta + stats.
    const opSize = Math.min(W * 0.08, 38);
    const panW = Math.min(W - 24, 560);
    this.opMaxW = panW - 24;
    this.panel(W / 2 - panW / 2, 66, panW, opSize * 1.7, worldTint, 0.88, 16);
    this.opText = this.text(W / 2, 66 + opSize * 0.85, '', opSize, CSS.white);

    // En vertical la etiqueta ocupa la banda superior, separada de la zona jugable.
    const missionH = 22;
    const missionY = top - 48;
    this.missionMaxW = panW - 18;
    this.hudCapsule(W / 2 - panW / 2, missionY, panW, missionH, worldTint, 11);
    this.missionText = this.text(W / 2, missionY + missionH / 2, this.getMissionLabel(), 12, CSS.white, 0.5, FONTS.friendly, '800');
    this.missionText.setScale(Math.min(1, this.missionMaxW / Math.max(1, this.missionText.width)));

    const statsW = Math.min(138, W * 0.34);
    this.hudCapsule(6, 6, statsW, 54, worldTint, 14);
    this.scoreText = this.text(14, 20, '', 20, CSS.gold, 0);
    this.streakText = this.text(14, 44, '', 15, CSS.magenta, 0, FONTS.friendly, '800');

    const vitalW = Math.min(146, W * 0.37);
    const vitalX = W - vitalW - 6;
    this.hudCapsule(vitalX, 6, vitalW, 54, worldTint, 14);
    this.timeText = this.text(W - 14, 20, '', 20, CSS.cyan, 1);
    this.lifeOrbs = [];
    for (let i = 0; i < LEVEL.MAX_LIVES; i++) {
      const orb = this.add.image(W - 18 - i * 26, 44, 'crystal').setScale(0.42).setTint(HEX.red);
      this.ui.add(orb);
      this.lifeOrbs.push(orb);
    }

    // En vertical se conserva una cápsula compacta justo antes del área jugable.
    const barW = W * 0.82;
    const progressY = top - 28;
    this.hudCapsule(W / 2 - barW / 2 - 8, progressY, barW + 16, 26, worldTint, 12);
    this.bar = { x: W / 2 - barW / 2, y: top - 9, w: barW, h: 5 };
    this.progFill = this.add.graphics();
    this.ui.add(this.progFill);
    this.progLabel = this.text(W / 2, top - 20, '', 15, CSS.white, 0.5, FONTS.friendly, '800');

    // Banda inferior: controles grandes (más grandes en celular).
    const cy = b.playY + b.playH + b.bottom * 0.5;
    const r = Math.min(64, W * 0.105, b.bottom * 0.3);
    if (this.isTouch) {
      this.ctrlButton(W * 0.16, cy, r, '◀', worldTint, 'left');
      this.ctrlButton(W * 0.43, cy, r, '▶', worldTint, 'right');
      this.ctrlButton(W * 0.83, cy, r, '▲', worldTint, 'jump');
    }
    this.buildMute(W - 28, H - 28, 20, 0.5);
    // Mantiene la misma relación espacial cuando el HUD se muestra en vertical.
    const pauseRadius = this.isTouch ? 24 : 22;
    const pauseGap = 10;
    this.buildPauseButton(
      6 + statsW / 2,
      6 + 54 + pauseGap + pauseRadius,
      pauseRadius,
    );
  }

  buildPauseButton(x, y, radius) {
    const color = this.worldTheme?.tint || HEX.cyan;
    const glow = this.add.circle(x, y, radius + 5, color, 0.12);
    const circle = this.add.circle(x, y, radius, 0x061329, 0.9).setStrokeStyle(2.5, color, 0.96)
      .setInteractive(new Phaser.Geom.Circle(radius, radius, Math.max(24, radius)), Phaser.Geom.Circle.Contains);
    const label = this.add.text(x, y - 1, '⏸', {
      fontFamily: FONTS.display,
      fontSize: `${Math.round(radius * 1.2)}px`,
      fontStyle: '900',
      color: CSS.white,
    }).setOrigin(0.5).setShadow(0, 2, '#020814', 3, true, true);
    this.ui.add([glow, circle, label]);
    circle.on('pointerdown', (_pointer, _x, _y, event) => {
      event?.stopPropagation?.();
      this.openPauseMenu();
    });
  }

  openPauseMenu() {
    if (this.pauseMenu || !this.ls?.pauseMission?.()) return;
    this.releaseControls();
    this.pauseMenu = new PauseMenu(this, {
      themeColor: this.worldTheme?.tint || HEX.cyan,
      onContinue: () => this.resumeFromPause(),
      onRestart: () => this.restartFromPause(),
      onExit: () => this.exitFromPause(),
    });
  }

  resumeFromPause() {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
    this.ls?.resumeMission?.();
  }

  restartFromPause() {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
    this.ls?.restartMission?.();
  }

  exitFromPause() {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
    this.releaseControls();
    this.ls?.exitMission?.();
  }

  buildMute(x, y, size, originX) {
    if (!this.audio) return;
    this.muteBtn = this.add
      .text(x, y, this.audio.muted ? '🔇' : '🔊', { fontFamily: FONTS.friendly, fontSize: `${Math.round(size + 6)}px`, color: CSS.dim })
      .setOrigin(originX, 0.5)
      .setPadding(8)
      .setInteractive({ useHandCursor: true });
    this.ui.add(this.muteBtn);
    this.muteBtn.on('pointerdown', () => {
      const m = this.audio.toggleMute();
      PlayerData.setSettings({ muted: m });
      this.muteBtn.setText(m ? '🔇' : '🔊');
    });
  }

  update() {
    if (!this.gs || !this.opText) return;
    const p = this.gs.problem;
    this.opText.setText(p ? `${p.text} = ?` : 'Avanza →');
    this.opText.setScale(Math.min(1, this.opMaxW / Math.max(1, this.opText.width)));
    this.scoreText.setText(`★ ${formatScore(this.gs.score)}`);
    this.streakText.setText(this.gs.streak > 0 ? `🔥 x${this.gs.streak}` : 'x0');

    const t = Math.ceil(this.gs.timeLeft);
    this.timeText.setText(`⏱ ${t}s`);
    if (t <= 15) this.timeText.setColor(CSS.red).setAlpha(0.6 + 0.4 * Math.abs(Math.sin(this.time.now / 200)));
    else this.timeText.setColor(CSS.cyan).setAlpha(1);

    this.lifeOrbs.forEach((orb, i) => {
      const alive = i < this.gs.lives;
      orb.setAlpha(alive ? 1 : 0.18).setTint(alive ? HEX.red : HEX.slate);
    });

    if (this.progFill && this.bar) {
      const { x, y, w, h } = this.bar;
      this.progFill.clear();
      this.progFill.fillStyle(0x150e38, 0.85);
      this.progFill.fillRoundedRect(x, y, w, h, h / 2);
      this.progFill.fillStyle(this.worldTheme?.tint || HEX.cyan, 1);
      this.progFill.fillRoundedRect(x, y, Math.max(6, w * this.gs.progress), h, h / 2);
    }
    if (this.progLabel) this.progLabel.setText(`Retos ${this.gs.solved}/${this.gs.totalChallenges}`);
  }
}
